class FudgeStage extends HTMLElement {
  connectedCallback() {
    // Header content can wrap or change in Theme Editor; use its rendered height.
    this.updateViewportOffset = () => {
      const headerHeight = ['.announcement', '.site-header'].reduce((height, selector) => {
        return height + (document.querySelector(selector)?.getBoundingClientRect().height || 0);
      }, 0);
      this.style.setProperty('--stage-viewport-offset', `${headerHeight}px`);
    };
    this.updateViewportOffset();
    this.headerObserver = new ResizeObserver(this.updateViewportOffset);
    ['.announcement', '.site-header'].forEach((selector) => {
      const element = document.querySelector(selector);
      if (element) this.headerObserver.observe(element, { box: 'border-box' });
    });
    this.allProducts = [...this.querySelectorAll('[data-stage-product]')];
    this.allDetails = [...this.querySelectorAll('[data-stage-detail]')];
    this.products = [...this.allProducts];
    this.details = [...this.allDetails];
    if (!this.products.length) return;
    this.index = 0;
    this.motionPreference = this.getMotionPreference();
    this.classList.toggle('force-motion', this.motionPreference === 'full');
    this.querySelector('[data-stage-prev]')?.addEventListener('click', () => this.move(-1));
    this.querySelector('[data-stage-next]')?.addEventListener('click', () => this.move(1));
    this.querySelectorAll('[data-stage-filter]').forEach((button) => {
      button.addEventListener('click', () => this.applyFilter(button.dataset.stageFilter));
    });
    this.flavorSelect = this.querySelector('[data-stage-flavor]');
    this.flavorJump = this.querySelector('[data-stage-flavor-jump]');
    this.flavorSelect?.addEventListener('change', () => this.applyFilter('all', this.flavorSelect.value));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.move(-1);
      if (event.key === 'ArrowRight') this.move(1);
    });
    this.productArea = this.querySelector('[data-stage-products]');
    this.swipeHint = this.querySelector('[data-swipe-hint]');
    this.setupSwipe();
    this.swipeHintTimer = window.setTimeout(() => this.dismissSwipeHint(), 3000);
    this.loadNearby(1);
    this.render();
    this.deferOuterImages();
  }

  disconnectedCallback() {
    this.headerObserver?.disconnect();
  }

  setupSwipe() {
    if (!this.productArea) return;
    let gesture = null;

    const resetGesture = (event) => {
      if (gesture && this.productArea.hasPointerCapture?.(gesture.pointerId)) {
        this.productArea.releasePointerCapture(gesture.pointerId);
      }
      gesture = null;
      this.productArea.classList.remove('is-swiping');
    };

    this.productArea.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || event.button !== 0) return;
      gesture = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startedAt: performance.now(),
        captured: false
      };
    });

    this.productArea.addEventListener('pointermove', (event) => {
      if (!gesture || event.pointerId !== gesture.pointerId || gesture.captured) return;
      const deltaX = event.clientX - gesture.startX;
      const deltaY = event.clientY - gesture.startY;
      if (Math.abs(deltaX) < 7 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.05) return;
      gesture.captured = true;
      this.productArea.setPointerCapture?.(event.pointerId);
      this.productArea.classList.add('is-swiping');
    });

    this.productArea.addEventListener('pointerup', (event) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      const deltaX = event.clientX - gesture.startX;
      const deltaY = event.clientY - gesture.startY;
      const elapsed = performance.now() - gesture.startedAt;
      const isHorizontal = Math.abs(deltaX) > Math.abs(deltaY) * .9;
      const crossedDistance = Math.abs(deltaX) >= 26;
      const isQuickFlick = elapsed <= 260 && Math.abs(deltaX) >= 16;
      if (isHorizontal && (crossedDistance || isQuickFlick)) {
        this.suppressProductClick = true;
        window.setTimeout(() => { this.suppressProductClick = false; }, 250);
        this.move(deltaX < 0 ? 1 : -1);
        this.dismissSwipeHint();
      }
      resetGesture(event);
    });

    this.productArea.addEventListener('pointercancel', resetGesture);
    this.productArea.addEventListener('dragstart', (event) => event.preventDefault());
    this.productArea.addEventListener('click', (event) => {
      if (this.suppressProductClick) {
        event.preventDefault();
        event.stopPropagation();
        this.suppressProductClick = false;
        return;
      }

      const product = event.target.closest('[data-stage-product]');
      const position = Number(product?.dataset.position);
      if (Math.abs(position) !== 1) return;
      event.preventDefault();
      event.stopPropagation();
      this.move(position);
      this.dismissSwipeHint();
    }, true);
  }

  dismissSwipeHint() {
    window.clearTimeout(this.swipeHintTimer);
    this.swipeHint?.classList.add('is-dismissed');
  }

  deferOuterImages() {
    const load = () => {
      if ('requestIdleCallback' in window) window.requestIdleCallback(() => this.loadNearby(2), { timeout: 1800 });
      else window.setTimeout(() => this.loadNearby(2), 400);
    };
    if (document.readyState === 'complete') load();
    else window.addEventListener('load', load, { once: true });
  }

  getMotionPreference() {
    const requested = new URLSearchParams(window.location.search).get('ffk_motion');
    if (requested === 'full' || requested === 'reduce') {
      try { window.sessionStorage.setItem('ffk_motion', requested); } catch (_) {}
      return requested;
    }
    try { return window.sessionStorage.getItem('ffk_motion') || 'auto'; } catch (_) { return 'auto'; }
  }

  applyFilter(filter, productId = '') {
    const patterns = {
      'dairy-free': ['dairy-free', 'dairyfree', 'vegan'],
      seasonal: ['seasonal'],
      'sugar-free': ['sugar-free', 'sugarfree']
    };
    this.products = this.allProducts.filter((product) => {
      if (filter === 'all') return true;
      const metadata = product.dataset.filterMetadata || '';
      return patterns[filter].some((pattern) => metadata.includes(pattern));
    });
    if (!this.products.length) return;

    const activeKeys = new Set(this.products.map((product) => product.dataset.productKey));
    this.allProducts.forEach((product) => { product.hidden = !activeKeys.has(product.dataset.productKey); });
    this.details = this.allDetails.filter((detail) => activeKeys.has(detail.dataset.productKey));
    this.allDetails.forEach((detail) => { detail.hidden = !activeKeys.has(detail.dataset.productKey); });
    this.querySelectorAll('[data-stage-filter]').forEach((button) => {
      const active = button.dataset.stageFilter === filter;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    if (this.flavorJump) this.flavorJump.hidden = filter !== 'all';
    const selectedIndex = productId
      ? this.products.findIndex((product) => product.dataset.productId === productId)
      : 0;
    this.index = selectedIndex >= 0 ? selectedIndex : 0;
    this.loadNearby();
    this.render();
  }

  move(direction) {
    if (this.isAnimating) {
      this.queuedDirection = direction;
      return;
    }
    const reducedMotion = this.motionPreference === 'reduce' || (this.motionPreference === 'auto' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const compactMotion = window.matchMedia('(max-width: 700px)').matches;
    const outgoingDetail = this.details[this.index];
    const before = reducedMotion ? null : new Map(this.products.map((product) => {
      const style = getComputedStyle(product);
      return [product, { transform: style.transform, opacity: style.opacity, filter: style.filter }];
    }));

    this.index = (this.index + direction + this.products.length) % this.products.length;
    this.loadNearby();
    this.classList.toggle('stage-moving-forward', direction > 0);
    this.classList.toggle('stage-moving-backward', direction < 0);
    this.classList.add('stage-is-animating');
    this.render(!reducedMotion);
    const incomingDetail = this.details[this.index];

    if (reducedMotion) {
      this.classList.remove('stage-is-animating', 'stage-moving-forward', 'stage-moving-backward');
      return;
    }

    this.getBoundingClientRect();
    const productAnimations = this.products.map((product) => {
      const start = before.get(product);
      const endStyle = getComputedStyle(product);
      if (Number(start.opacity) === 0 && Number(endStyle.opacity) === 0) return null;
      return product.animate(
        [
          { transform: start.transform, opacity: start.opacity, filter: start.filter },
          { transform: endStyle.transform, opacity: endStyle.opacity, filter: endStyle.filter }
        ],
        { duration: compactMotion ? 460 : 700, easing: 'cubic-bezier(.22,.75,.18,1)', fill: 'none' }
      );
    }).filter(Boolean);
    const detailTransition = this.animateDetails(outgoingDetail, incomingDetail, compactMotion ? 320 : 500);
    this.isAnimating = true;
    Promise.allSettled([...productAnimations.map((animation) => animation.finished), detailTransition]).then(() => {
      this.isAnimating = false;
      this.classList.remove('stage-is-animating', 'stage-moving-forward', 'stage-moving-backward');
      if (this.queuedDirection) {
        const queuedDirection = this.queuedDirection;
        this.queuedDirection = 0;
        window.requestAnimationFrame(() => this.move(queuedDirection));
      }
    });
  }

  animateDetails(outgoing, incoming, duration = 500) {
    if (!outgoing || !incoming || outgoing === incoming) return Promise.resolve();
    const options = { duration, easing: 'ease', fill: 'none' };
    const outgoingAnimation = outgoing.animate(
      [{ opacity: 1 }, { opacity: 0 }],
      options
    );
    return outgoingAnimation.finished.then(() => {
      outgoing.classList.remove('is-active');
      incoming.classList.add('is-active');
      outgoingAnimation.cancel();
      return incoming.animate([{ opacity: 0 }, { opacity: 1 }], options).finished;
    });
  }

  loadNearby(radius = 2) {
    for (let offset = -radius; offset <= radius; offset += 1) {
      const index = (this.index + offset + this.products.length) % this.products.length;
      const image = this.products[index]?.querySelector('[data-stage-image]');
      if (!image || image.dataset.loaded === 'true') continue;
      image.src = image.dataset.src;
      image.srcset = image.dataset.srcset;
      image.dataset.loaded = 'true';
    }
  }

  render(preserveDetails = false) {
    const length = this.products.length;
    this.products.forEach((product, index) => {
      let position = index - this.index;
      if (position > length / 2) position -= length;
      if (position < -length / 2) position += length;
      product.dataset.position = String(Math.max(-3, Math.min(3, position)));
      product.setAttribute('aria-hidden', position !== 0 ? 'true' : 'false');
      product.querySelectorAll('a,button').forEach((control) => { control.tabIndex = position === 0 ? 0 : -1; });
    });
    if (!preserveDetails) {
      this.allDetails.forEach((detail) => detail.classList.remove('is-active'));
      this.details[this.index]?.classList.add('is-active');
    }
    if (this.flavorSelect && this.products[this.index]?.dataset.productId) {
      this.flavorSelect.value = this.products[this.index].dataset.productId;
    }
  }
}
customElements.define('fudge-stage', FudgeStage);
